import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { expensesAPI, categoriesAPI } from '../api';
import useAuthStore from '../store/auth';
import { formatAmount } from '../utils/currency';

export default function ExpensesPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const currency = user?.currency || 'EUR';
  const fmt = n => formatAmount(n, currency);

  const [items, setItems]         = useState([]);
  const [cats, setCats]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [month, setMonth]         = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm]           = useState({ amount:'', category_id:'', date:new Date().toISOString().split('T')[0], description:'' });

  useEffect(() => { load(); loadCats(); }, [month]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await expensesAPI.list({ month, category_id: catFilter || undefined }); setItems(data); }
    catch { toast.error(t('error')); }
    finally { setLoading(false); }
  };
  const loadCats = async () => {
    try { const { data } = await categoriesAPI.list(); setCats(data); } catch {}
  };

  const openAdd  = () => { setEditing(null); setForm({ amount:'', category_id:cats[0]?.id||'', date:new Date().toISOString().split('T')[0], description:'' }); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({ amount:item.amount, category_id:item.category_id, date:item.date?.split('T')[0], description:item.description||'' }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.amount || !form.category_id || !form.date) return toast.error(t('required'));
    try {
      if (editing) { await expensesAPI.update(editing.id, form); }
      else         { await expensesAPI.create(form); }
      toast.success(t('expense_saved')); setShowModal(false); load();
    } catch { toast.error(t('error')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await expensesAPI.remove(id); toast.success(t('expense_deleted')); load(); }
    catch { toast.error(t('error')); }
  };

  const total = items.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const inp = { width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', color:'var(--text)', fontSize:14, outline:'none', boxSizing:'border-box' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontFamily:'Syne,sans-serif', fontSize:24, fontWeight:700 }}>{t('expenses')}</h1>
          <p style={{ color:'var(--text2)', fontSize:14, marginTop:4 }}>Total : <strong style={{ color:'var(--danger)' }}>{fmt(total)}</strong></p>
        </div>
        <button onClick={openAdd} style={{ padding:'10px 20px', background:'var(--danger)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>
          + {t('add')}
        </button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', color:'var(--text)', fontSize:14, outline:'none' }} />
        <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setTimeout(load, 100); }}
          style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', color:'var(--text)', fontSize:14, outline:'none' }}>
          <option value="">Toutes les categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
        {loading ? <p style={{ color:'var(--text2)', textAlign:'center', padding:40 }}>{t('loading')}</p>
        : items.length === 0 ? (
          <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📉</div>
            <p>{t('no_expenses')}</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>{[t('date'),t('category'),t('description'),t('amount'),t('actions')].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'10px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:12, color:'var(--text2)', fontSize:14 }}>{new Date(item.date).toLocaleDateString('fr-FR')}</td>
                    <td style={{ padding:12 }}>
                      <span style={{ padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600, background:(item.category_color||'#f76a6a')+'20', color:item.category_color||'var(--danger)' }}>
                        {item.category_icon} {item.category_name || t('category')}
                      </span>
                    </td>
                    <td style={{ padding:12, fontSize:14, color:'var(--text2)' }}>{item.description || '-'}</td>
                    <td style={{ padding:12, fontFamily:'Syne,sans-serif', fontWeight:700, color:'var(--danger)' }}>-{fmt(item.amount)}</td>
                    <td style={{ padding:12 }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => openEdit(item)} style={{ padding:'5px 10px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12, cursor:'pointer' }}>{t('edit')}</button>
                        <button onClick={() => handleDelete(item.id)} style={{ padding:'5px 10px', background:'var(--danger)', color:'#fff', border:'none', borderRadius:8, fontSize:12, cursor:'pointer' }}>{t('delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000090', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:32, width:'100%', maxWidth:480 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ fontFamily:'Syne,sans-serif', fontWeight:700 }}>{editing ? t('edit_expense') : t('add_expense')}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'var(--text2)', cursor:'pointer' }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('amount')}</label>
                <input type="number" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('category')}</label>
                <select value={form.category_id} onChange={e => setForm({...form, category_id:e.target.value})} style={inp}>
                  <option value="">-- Choisir --</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('date')}</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} style={inp} />
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('description')}</label>
              <textarea value={form.description} onChange={e => setForm({...form, description:e.target.value})} style={{ ...inp, minHeight:80, resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setShowModal(false)} style={{ flex:1, padding:14, background:'var(--surface2)', color:'var(--text)', border:'1px solid var(--border)', borderRadius:10, cursor:'pointer' }}>{t('cancel')}</button>
              <button onClick={handleSave} style={{ flex:1, padding:14, background:'var(--danger)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}